import type { EmployeeTask } from "@/features/separation/api";
import type { EmployeeProfile } from "@/features/profile/types";
import { isUaeNational } from "@/features/profile/wizard/steps";

/**
 * The papers everybody must have on file, and where each one stands.
 *
 * Since November 2026: the police clearance the employee brings, the AML
 * screening and the UAE PNG check HR runs, the family book of a UAE national,
 * and the medical insurance card, which no longer holds up a sign-up. The API
 * keeps a task open for each one missing once somebody is on the books, and
 * closes it when the file arrives, whoever attached it.
 */

export type DocumentOwner = "employee" | "admin";

export interface RequiredDocument {
  /** The upload slot, as the API names it. */
  kind: string;
  label: string;
  /** The profile field the copy is kept in. */
  field: keyof EmployeeProfile;
  /** Who supplies it. HR can attach any of them. */
  owner: DocumentOwner;
  nationalsOnly?: boolean;
}

export const REQUIRED_DOCUMENTS: readonly RequiredDocument[] = [
  {
    kind: "police_clearance",
    label: "Police clearance certificate",
    field: "police_clearance_file_id",
    owner: "employee",
  },
  {
    kind: "family_book",
    label: "Family book",
    field: "family_book_file_id",
    owner: "employee",
    nationalsOnly: true,
  },
  { kind: "aml_screening", label: "AML screening", field: "aml_screening_file_id", owner: "admin" },
  { kind: "uae_png", label: "UAE PNG", field: "uae_png_file_id", owner: "admin" },
  {
    kind: "health_insurance",
    label: "Medical insurance",
    field: "health_insurance_file_id",
    owner: "admin",
  },
];

const PREFIX = "document:";

/** A task that asks for one of these papers rather than a signature. */
export function isDocumentTask(task: Pick<EmployeeTask, "key">): boolean {
  return task.key.startsWith(PREFIX);
}

/** The upload slot a document task asks for. */
export function documentKind(task: Pick<EmployeeTask, "key">): string {
  return task.key.slice(PREFIX.length);
}

export type DocumentState = "uploaded" | "waiting" | "not_needed";

export interface RequiredDocumentRow extends RequiredDocument {
  fileId: string | null;
  state: DocumentState;
  task: EmployeeTask | undefined;
}

/**
 * Each paper this person is asked for, with its file and its task.
 *
 * The file decides: a paper on the record is uploaded, whatever its task
 * says. The family book is listed for a UAE national, or for anybody who has
 * one on file or was once asked for it.
 */
export function requiredDocumentRows(
  profile: EmployeeProfile,
  tasks: readonly EmployeeTask[] | undefined,
): RequiredDocumentRow[] {
  const byKey = new Map(
    (tasks ?? []).filter(isDocumentTask).map((task) => [documentKind(task), task]),
  );
  const national = isUaeNational(profile.nationality);
  return REQUIRED_DOCUMENTS.flatMap((document) => {
    const fileId = (profile[document.field] as string | null | undefined) ?? null;
    const task = byKey.get(document.kind);
    if (document.nationalsOnly && !national && !fileId && !task) return [];
    const state: DocumentState = fileId
      ? "uploaded"
      : task?.status === "skipped" || (document.nationalsOnly && !national)
        ? "not_needed"
        : "waiting";
    return [{ ...document, fileId, state, task }];
  });
}

/** What the employee still has to upload themselves: their open document tasks. */
export function owedByEmployee(tasks: readonly EmployeeTask[] | undefined): EmployeeTask[] {
  return (tasks ?? []).filter(
    (task) => isDocumentTask(task) && task.owner === "employee" && task.status === "pending",
  );
}
